/* eslint-disable max-lines */
import { useQueryClient } from "@tanstack/react-query";
import { useAtom, useAtomValue, useSetAtom } from "jotai";
import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";

import {
  activePinIdAtom,
  currentPinningSessionAtom,
  escapeHatchTriggerAtom,
  hoveredPinIdAtom,
  hoveredPinSourceAtom,
  pendingInitialMessageAtom,
  pinningFeedbackLastEditedPinIdAtom,
  pinningImageApprovalAtom,
  pinningImageLoadedAtom,
  pinningMobileViewAtom,
  pinningModeAtom,
  pinningPanelExpandedAtom,
  pinningSessionsAtom,
  pinsAtom,
  pinsWithErrorsAtom,
  useApprovePin,
  useCompletePinningSession,
  useRejectPin,
  useRemovePin,
  useSubmitPinMessage,
  useUpdatePinInstruction,
  type AcknowledgmentPayload,
  type PinMessage,
  type Pin as PinType,
  type PinState,
  type ResultPayload
} from "@design-lab/entities/pinning";
import { useCompleteRefineSession } from "@design-lab/features/design-artwork/api/useManualBackgroundRemoval";
import {
  trackDesignLabPinClarificationClicked,
  trackDesignLabPinDeleteFailed,
  trackDesignLabPinDeleted,
  trackDesignLabPinError,
  trackDesignLabPinMessageSubmitted,
  trackDesignLabPinNeedsClarification,
  trackDesignLabPinReady,
  trackDesignLabPinSelected,
  trackDesignLabPinUnsupported,
  trackDesignLabPinningSessionCompleted,
  trackDesignLabPinningSessionCompletionFailed,
  trackPinSessionsGenConfirmed,
  trackPinSessionsGenDiscarded,
  type AnalyticsClarification
} from "@design-lab/shared/lib/analytics";
import { useResponsive } from "@design-lab/shared/hooks/useResponsive";
import { useIsStaff } from "@design-lab/shared/lib/config";
import { useToast } from "@design-lab/shared/ui";
import { useArtboard } from "@design-lab/widgets/artboard/model/useArtboard";
import { generateUUID } from "common/utils/uuid";

import { getClarificationAnswer } from "./PinClarifications";

const MIN_MESSAGE_LENGTH = 1;

export type ClarificationResponse = {
  selected: string | null;
  custom: string;
  isTypingCustom: boolean;
};

function getMessageCounts(
  messages: PinMessage[],
  pendingRole?: "user" | "ai"
): { userMessagesCount: number; aiMessagesCount: number } {
  const userMessagesCount =
    messages.filter(m => m.role === "user").length + (pendingRole === "user" ? 1 : 0);
  const aiMessagesCount =
    messages.filter(m => m.role === "ai").length + (pendingRole === "ai" ? 1 : 0);
  return { userMessagesCount, aiMessagesCount };
}

// eslint-disable-next-line max-lines-per-function
export function usePinningPanel() {
  const { isDesktop } = useResponsive();
  const { placement } = useArtboard();
  const side = placement === "back" ? "back" : "front";
  const currentPinningSession = useAtomValue(currentPinningSessionAtom);
  const currentArtworkId = currentPinningSession?.artworkId;
  const currentSessionId = currentPinningSession?.sessionId;
  const currentDesignProjectPlacementId = currentPinningSession?.designProjectPlacementId;

  const [imageApproval, setImageApproval] = useAtom(pinningImageApprovalAtom);
  const setPinningMode = useSetAtom(pinningModeAtom);
  const setPinningSessions = useSetAtom(pinningSessionsAtom);
  const setCurrentPinningSession = useSetAtom(currentPinningSessionAtom);
  const { mutateAsync: completeRefine, isPending: isRefining } = useCompleteRefineSession(side);
  const { mutateAsync: approvePinMutate, isPending: isApprovingPin } = useApprovePin();
  const { mutateAsync: rejectPinMutate, isPending: isRejectingPin } = useRejectPin();
  const { mutate: completeSession, isPending: isCompletingSession } = useCompletePinningSession();
  const isImageLoaded = useAtomValue(pinningImageLoadedAtom);
  const isApprovingImage = isApprovingPin || isRejectingPin || isRefining;
  const isImageLoading = imageApproval !== null && !isImageLoaded;
  const isRejectingImage = isRejectingPin;

  const [pins, setPins] = useAtom(pinsAtom);
  const [activePinId, setActivePinId] = useAtom(activePinIdAtom);
  const setHoveredPinId = useSetAtom(hoveredPinIdAtom);
  const setHoveredPinSource = useSetAtom(hoveredPinSourceAtom);
  const [pendingInitialMessage, setPendingInitialMessage] = useAtom(pendingInitialMessageAtom);
  const [composerText, setComposerText] = useState("");
  const [removingPinIds, setRemovingPinIds] = useState<Set<string>>(new Set());
  const [pendingDeletePinId, setPendingDeletePinId] = useState<string | null>(null);
  const [clarificationResponses, setClarificationResponses] = useState<
    Record<string, ClarificationResponse>
  >({});
  const [mobileView, setMobileView] = useAtom(pinningMobileViewAtom);
  const [isPanelExpanded, setIsPanelExpanded] = useAtom(pinningPanelExpandedAtom);
  const [editingPinId, setEditingPinId] = useState<string | null>(null);
  const setLastEditedPinId = useSetAtom(pinningFeedbackLastEditedPinIdAtom);

  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const pendingCursorPositionRef = useRef<number | null>(null);
  const clarificationScrollRef = useRef<HTMLDivElement>(null);
  const messagesScrollRef = useRef<HTMLDivElement>(null);
  const pinsContainerRef = useRef<HTMLDivElement>(null);
  const savedClarificationResponsesRef = useRef<Record<string, ClarificationResponse> | null>(null);
  const savedPinStateRef = useRef<PinType["state"] | null>(null);
  const savedMessagesRef = useRef<PinMessage[] | null>(null);
  const savedComposerTextRef = useRef<string | null>(null);
  const [unansweredAbove, setUnansweredAbove] = useState(0);
  const [unansweredBelow, setUnansweredBelow] = useState(0);
  const isStaff = useIsStaff();
  // Stable ref to the latest isPinActive check so async retry loops
  // (which outlive the render that started them) always read current state.
  const isPinActive = useCallback(
    (id: string) => pins.some(p => p.id === id) && !removingPinIds.has(id),
    [pins, removingPinIds]
  );
  const isPinActiveRef = useRef(isPinActive);
  isPinActiveRef.current = isPinActive;

  const setEscapeHatchTrigger = useSetAtom(escapeHatchTriggerAtom);
  const setPinsWithErrors = useSetAtom(pinsWithErrorsAtom);
  const pinsWithErrorsRef = useRef<Set<string>>(new Set());
  const slowResponseTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const abortControllerRef = useRef<AbortController | null>(null);
  const autoSubmitFinalizeRef = useRef(false);

  useEffect(
    () => () => {
      if (slowResponseTimerRef.current) {
        clearTimeout(slowResponseTimerRef.current);
        slowResponseTimerRef.current = null;
      }
    },
    []
  );

  const queryClient = useQueryClient();
  const { mutate: removePinApi } = useRemovePin();
  const { mutate: updatePinInstructionApi, isPending: isUpdatingPinInstruction } =
    useUpdatePinInstruction();
  const { error: showErrorToast } = useToast();

  const activePin = useMemo(
    () => pins.find(p => p.id === activePinId) ?? null,
    [activePinId, pins]
  );

  // Reset expanded state when going to list view or when active pin becomes ready
  useEffect(() => {
    if (mobileView === "list") {
      setIsPanelExpanded(false);
    }
  }, [mobileView, setIsPanelExpanded]);

  // Auto-scroll messages to bottom when messages change
  useEffect(() => {
    if (messagesScrollRef.current && activePin?.messages.length) {
      messagesScrollRef.current.scrollTop = messagesScrollRef.current.scrollHeight;
    }
  }, [activePin?.messages.length, activePin?.state]);

  useEffect(() => {
    if (imageApproval) {
      setIsPanelExpanded(false);
      return;
    }
    if (activePin?.state === "ready") {
      setIsPanelExpanded(false);
    }
  }, [activePin?.state, setIsPanelExpanded, imageApproval]);

  const clearRemovingPin = useCallback((pinId: string) => {
    setRemovingPinIds(prev => {
      const next = new Set(prev);
      next.delete(pinId);
      return next;
    });
  }, []);

  // Clear pins when unmounting
  useEffect(
    () => () => {
      abortControllerRef.current?.abort();
      setPins([]);
      setActivePinId(null);
      setLastEditedPinId(null);
    },
    [setPins, setActivePinId, setLastEditedPinId]
  );

  // Reset composer when active pin changes
  useEffect(() => {
    setComposerText("");
    setClarificationResponses({});
  }, [activePinId]);

  // Auto-focus composer
  useEffect(() => {
    const shouldFocus =
      (activePin?.state === "draft" && activePin.messages.length === 0) ||
      activePin?.state === "needs_clarification";

    if (shouldFocus && textareaRef.current) {
      setTimeout(() => {
        textareaRef.current?.focus();
        textareaRef.current?.scrollIntoView({ behavior: "smooth", block: "nearest" });
      }, 100);
    }
  }, [activePin?.state, activePin?.messages.length, activePinId]);

  // Handle cursor positioning after newline
  useLayoutEffect(() => {
    if (pendingCursorPositionRef.current !== null && textareaRef.current) {
      const pos = pendingCursorPositionRef.current;
      textareaRef.current.selectionStart = pos;
      textareaRef.current.selectionEnd = pos;
      textareaRef.current.scrollTop = textareaRef.current.scrollHeight;
      pendingCursorPositionRef.current = null;
    }
  }, [composerText]);

  const createUuid = useCallback(() => generateUUID(), []);

  const updatePin = useCallback(
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    (pinId: string, updater: (pin: PinType) => any) => {
      setPins(prev => prev.map(pin => (pin.id === pinId ? updater(pin) : pin)));
    },
    [setPins]
  );

  const deletePin = useCallback(
    (pinId: string) => {
      setPins(prev => prev.filter(p => p.id !== pinId));
      if (activePinId === pinId) {
        setActivePinId(null);
      }
      if (imageApproval?.pinId === pinId) {
        setImageApproval(null);
      }
    },
    [activePinId, setActivePinId, setPins, imageApproval, setImageApproval]
  );

  const handleDeletePin = useCallback(
    (pinId: string) => {
      const deletedPin = pins.find(p => p.id === pinId);
      if (deletedPin?.state === "processing") {
        abortControllerRef.current?.abort();
      }

      // trigger escape hatch when deleting a pin that had an error
      if (pinsWithErrorsRef.current.has(pinId)) {
        setEscapeHatchTrigger(prev => prev ?? "error");
      }

      const sessionId = currentSessionId;

      if (!sessionId) {
        deletePin(pinId);
        return;
      }

      const pin = pins.find(p => p.id === pinId);
      const hasBeenSyncedToBackend = (pin?.messages?.length ?? 0) > 0 || pin?.state !== "draft";

      if (!hasBeenSyncedToBackend) {
        if (currentArtworkId && currentSessionId) {
          trackDesignLabPinDeleted({
            artworkId: currentArtworkId,
            sessionId: currentSessionId,
            pinId,
            pinState: pin?.state ?? "draft",
            hadMessages: false,
            isStaff
          });
        }
        deletePin(pinId);
        if (!isDesktop) {
          setMobileView("list");
        }
        return;
      }

      if (removingPinIds.has(pinId)) {
        return;
      }

      setRemovingPinIds(prev => new Set(prev).add(pinId));

      removePinApi(
        { sessionId, pinId },
        {
          onSuccess: () => {
            if (currentArtworkId && currentSessionId) {
              trackDesignLabPinDeleted({
                artworkId: currentArtworkId,
                sessionId: currentSessionId,
                pinId,
                pinState: pin?.state ?? "draft",
                hadMessages: (pin?.messages?.length ?? 0) > 0,
                isStaff
              });
            }
            deletePin(pinId);
            clearRemovingPin(pinId);
            if (!isDesktop) {
              setMobileView("list");
            }
          },
          onError: () => {
            if (currentArtworkId && currentSessionId) {
              trackDesignLabPinDeleteFailed({
                artworkId: currentArtworkId,
                sessionId: currentSessionId,
                pinId,
                isStaff
              });
            }
            clearRemovingPin(pinId);
            showErrorToast({
              title: "Failed to remove pin",
              description: "Something went wrong. Please try again."
            });
          }
        }
      );
    },
    [
      currentArtworkId,
      currentSessionId,
      removePinApi,
      deletePin,
      pins,
      removingPinIds,
      clearRemovingPin,
      showErrorToast,
      isDesktop,
      setMobileView,
      setEscapeHatchTrigger,
      isStaff
    ]
  );

  const openDeletePinModal = useCallback((pinId: string) => {
    setPendingDeletePinId(pinId);
  }, []);

  const closeDeletePinModal = useCallback(() => {
    setPendingDeletePinId(null);
  }, []);

  const confirmDeletePin = useCallback(() => {
    if (!pendingDeletePinId) return;
    handleDeletePin(pendingDeletePinId);
    setPendingDeletePinId(null);
  }, [handleDeletePin, pendingDeletePinId]);

  const openEditPinModal = useCallback((pinId: string) => {
    setEditingPinId(pinId);
  }, []);

  const closeEditPinModal = useCallback(() => {
    setEditingPinId(null);
  }, []);

  const editingPin = useMemo(
    () => pins.find(p => p.id === editingPinId) ?? null,
    [editingPinId, pins]
  );

  const handleUpdatePinInstruction = useCallback(
    (instruction: string) => {
      if (!editingPinId || !currentSessionId) return;

      updatePinInstructionApi(
        { sessionId: currentSessionId, pinId: editingPinId, instruction },
        {
          onSuccess: () => {
            setPins(prev =>
              prev.map(pin => (pin.id === editingPinId ? { ...pin, instruction } : pin))
            );
            setEditingPinId(null);
          },
          onError: () => {
            showErrorToast({
              title: "Failed to update pin",
              description: "Something went wrong. Please try again."
            });
          }
        }
      );
    },
    [editingPinId, currentSessionId, updatePinInstructionApi, setPins, showErrorToast]
  );

  const { mutate: submitMessage } = useSubmitPinMessage({
    onAcknowledgment: (payload: AcknowledgmentPayload) => {
      if (slowResponseTimerRef.current) {
        clearTimeout(slowResponseTimerRef.current);
        slowResponseTimerRef.current = null;
      }
      if (!activePinId) return;
      updatePin(activePinId, pin => ({
        ...pin,
        thinkingText: payload.message || undefined
      }));
    },
    onResult: (payload: ResultPayload) => {
      if (slowResponseTimerRef.current) {
        clearTimeout(slowResponseTimerRef.current);
        slowResponseTimerRef.current = null;
      }
      if (!activePinId) return;

      const knownStatuses = ["ready", "preview_ready", "needs_clarification", "unsupported"];
      const mappedStatus = knownStatuses.includes(payload.status) ? payload.status : "ready";
      const isNonGenerativeReady = payload.status === "ready" && !payload.generatedImageUrl;
      let nextState = mappedStatus as PinState;
      if (isNonGenerativeReady) {
        nextState = "processing";
      } else if (mappedStatus === "preview_ready") {
        nextState = "ready";
      }

      const clarifications = payload.clarifications ?? [];
      const resultMessage = payload.message || "Done.";
      const normalizedInstruction =
        payload.instruction === "Finalized request" && payload.currentSummary
          ? payload.currentSummary
          : payload.instruction;

      const currentPin = pins.find(p => p.id === activePinId);
      const { userMessagesCount, aiMessagesCount } = getMessageCounts(
        currentPin?.messages ?? [],
        "ai"
      );

      const askingClarifications = clarifications.filter(
        c => c.status === "asking" && c.options.length > 0
      );
      const hasClarifications =
        nextState === "needs_clarification" && askingClarifications.length > 0;

      updatePin(activePinId, pin => {
        const areasOfInterestUpdate = payload.aoiResult
          ? {
              areasOfInterest: payload.aoiResult,
              highlightAreasEnabled: pin.highlightAreasEnabled ?? true
            }
          : {};

        return {
          ...pin,
          state: nextState,
          instruction: normalizedInstruction,
          thinkingText: undefined,
          ...areasOfInterestUpdate,
          messages: [
            ...pin.messages,
            {
              id: createUuid(),
              role: "ai",
              content: resultMessage,
              timestamp: Date.now(),
              ...(hasClarifications && { clarifications, textInfo: payload.text })
            }
          ]
        };
      });

      if (hasClarifications) {
        setClarificationResponses({});
      }

      // Clear saved refs on success - no longer needed for error recovery
      savedClarificationResponsesRef.current = null;
      savedPinStateRef.current = null;
      savedMessagesRef.current = null;
      savedComposerTextRef.current = null;

      if (currentArtworkId && currentSessionId) {
        const trackingData = {
          artworkId: currentArtworkId,
          sessionId: currentSessionId,
          pinId: activePinId,
          userMessagesCount,
          aiMessagesCount
        };

        if (nextState === "ready" || isNonGenerativeReady) {
          trackDesignLabPinReady(trackingData);
        } else if (nextState === "unsupported") {
          trackDesignLabPinUnsupported(trackingData);
        } else if (nextState === "needs_clarification") {
          trackDesignLabPinNeedsClarification({
            ...trackingData,
            optionsCount: askingClarifications.reduce((sum, c) => sum + c.options.length, 0),
            questionType: askingClarifications.map(c => c.type).join(",")
          });
        }
      }

      if (
        payload.status === "preview_ready" &&
        payload.generatedImageUrl &&
        currentSessionId &&
        currentDesignProjectPlacementId
      ) {
        setImageApproval({
          pinId: activePinId,
          imageUrl: payload.generatedImageUrl,
          sessionId: currentSessionId,
          designProjectPlacementId: currentDesignProjectPlacementId,
          summary: payload.currentSummary ?? payload.instruction ?? null
        });
      }
      // Non-generative "ready" auto-complete is deferred to onStreamEnd
      // so the backend has time to send a follow-up "preview_ready" event.
    },
    onStreamEnd: () => {
      // Stream closed without a preview_ready event — transition to "ready"
      // so the escalation card appears.
      if (!activePinId) return;
      updatePin(activePinId, p => (p.state === "processing" ? { ...p, state: "ready" } : p));
    },
    onError: () => {
      if (slowResponseTimerRef.current) {
        clearTimeout(slowResponseTimerRef.current);
        slowResponseTimerRef.current = null;
      }
      if (!activePinId) return;

      // Condition A: trigger escape hatch on AI error
      setEscapeHatchTrigger(prev => prev ?? "error");
      pinsWithErrorsRef.current = new Set(pinsWithErrorsRef.current).add(activePinId);
      setPinsWithErrors(pinsWithErrorsRef.current);

      const currentPin = pins.find(p => p.id === activePinId);
      const { userMessagesCount, aiMessagesCount } = getMessageCounts(
        currentPin?.messages ?? [],
        "ai"
      );

      if (currentArtworkId && currentSessionId) {
        trackDesignLabPinError({
          artworkId: currentArtworkId,
          sessionId: currentSessionId,
          pinId: activePinId,
          errorType: "processing_error",
          userMessagesCount,
          aiMessagesCount
        });
      }

      // Show error toast instead of inline message
      showErrorToast({
        title: "Something went wrong",
        description: "Please try again."
      });

      // Reset chat state: restore previous messages (including clarifications) and state
      // This removes the user message that was added during submission and reverts to pre-submission state
      const previousState = savedPinStateRef.current ?? "draft";
      const previousMessages = savedMessagesRef.current;

      // Always use saved messages if available, otherwise remove the last message (user message added during submission)
      const messagesToRestore = previousMessages ?? currentPin?.messages.slice(0, -1) ?? [];

      updatePin(activePinId, pin => ({
        ...pin,
        state: previousState,
        thinkingText: undefined,
        messages: messagesToRestore
      }));

      // Restore clarification responses if they were saved
      if (savedClarificationResponsesRef.current) {
        setClarificationResponses(savedClarificationResponsesRef.current);
        savedClarificationResponsesRef.current = null;
      }

      // Restore composer text if it was saved
      if (savedComposerTextRef.current !== null) {
        setComposerText(savedComposerTextRef.current);
        savedComposerTextRef.current = null;
      }

      savedPinStateRef.current = null;
      savedMessagesRef.current = null;
    }
  });

  const isComposerTextValid = useCallback(
    (text: string) => text.trim().length >= MIN_MESSAGE_LENGTH,
    []
  );

  const lastAiMessageWithClarifications = useMemo(() => {
    if (!activePin) return null;
    for (let i = activePin.messages.length - 1; i >= 0; i -= 1) {
      const msg = activePin.messages[i];
      if (msg?.role === "ai" && msg.clarifications && msg.clarifications.length > 0) return msg;
    }
    return null;
  }, [activePin]);

  const pendingPillClarifications = useMemo(() => {
    const clarifications = lastAiMessageWithClarifications?.clarifications ?? [];
    return clarifications.filter(c => c.status === "asking" && c.options.length > 0);
  }, [lastAiMessageWithClarifications]);

  const fontClarification = useMemo(
    () => pendingPillClarifications.find(c => c.type === "font_selection"),
    [pendingPillClarifications]
  );

  const colorClarification = useMemo(
    () => pendingPillClarifications.find(c => c.type === "font_color"),
    [pendingPillClarifications]
  );

  const selectedFontValue = useMemo(() => {
    if (!fontClarification) return undefined;
    const response = clarificationResponses[fontClarification.question];
    if (response?.isTypingCustom) return response.custom || undefined;
    if (response?.selected) {
      const opt = fontClarification.options.find(o => o.label === response.selected);
      return opt?.value || response.selected;
    }
    return undefined;
  }, [fontClarification, clarificationResponses]);

  const selectedColorValue = useMemo(() => {
    if (!colorClarification) return undefined;
    const response = clarificationResponses[colorClarification.question];
    if (response?.isTypingCustom) return response.custom || undefined;
    if (response?.selected) {
      const opt = colorClarification.options.find(
        o => `${o.label} (${o.value})` === response.selected
      );
      return opt?.value || response.selected;
    }
    return undefined;
  }, [colorClarification, clarificationResponses]);

  const calculateUnansweredPositions = useCallback(() => {
    const scrollContainer = clarificationScrollRef.current;
    if (!scrollContainer) return;

    const containerRect = scrollContainer.getBoundingClientRect();
    let above = 0;
    let below = 0;

    pendingPillClarifications.forEach(clarification => {
      const isUnanswered = !getClarificationAnswer(clarificationResponses[clarification.question]);

      if (isUnanswered) {
        const labelValue = clarification.label || clarification.question;
        const element = scrollContainer.querySelector(
          `[data-clarification-label="${CSS.escape(labelValue)}"]`
        );
        if (element) {
          const elementRect = element.getBoundingClientRect();
          if (elementRect.bottom < containerRect.top) {
            above += 1;
          } else if (elementRect.top > containerRect.bottom) {
            below += 1;
          }
        }
      }
    });

    setUnansweredAbove(above);
    setUnansweredBelow(below);
  }, [pendingPillClarifications, clarificationResponses]);

  const scrollToFirstUnanswered = useCallback(
    (direction: "above" | "below") => {
      const scrollContainer = clarificationScrollRef.current;
      if (!scrollContainer) return;

      const containerRect = scrollContainer.getBoundingClientRect();

      for (const clarification of pendingPillClarifications) {
        const isUnanswered = !getClarificationAnswer(
          clarificationResponses[clarification.question]
        );

        if (isUnanswered) {
          const labelValue = clarification.label || clarification.question;
          const element = scrollContainer.querySelector(
            `[data-clarification-label="${CSS.escape(labelValue)}"]`
          );

          if (element) {
            const elementRect = element.getBoundingClientRect();
            const isTargetDirection =
              direction === "above"
                ? elementRect.bottom < containerRect.top
                : elementRect.top > containerRect.bottom;

            if (isTargetDirection) {
              element.scrollIntoView({ behavior: "smooth", block: "start" });
              break;
            }
          }
        }
      }
    },
    [pendingPillClarifications, clarificationResponses]
  );

  useEffect(() => {
    if (pendingPillClarifications.length > 0) {
      calculateUnansweredPositions();
    }
  }, [pendingPillClarifications.length, clarificationResponses, calculateUnansweredPositions]);

  const handleSelectClarificationOption = useCallback(
    (question: string, value: string | null) => {
      setClarificationResponses(prev => {
        const wasUnanswered = !getClarificationAnswer(prev[question]);

        const newResponses = {
          ...prev,
          [question]: {
            selected: value,
            custom: prev[question]?.custom ?? "",
            isTypingCustom: false
          }
        };

        if (currentArtworkId && currentSessionId && activePinId && activePin) {
          trackDesignLabPinClarificationClicked({
            artworkId: currentArtworkId,
            sessionId: currentSessionId,
            pinId: activePinId,
            optionValue: value || "",
            turnIndex: activePin.messages.filter(m => m.role === "user").length + 1,
            userMessagesCount: activePin.messages.filter(m => m.role === "user").length,
            aiMessagesCount: activePin.messages.filter(m => m.role === "ai").length,
            optionType: "suggested"
          });
        }

        if (value !== null && wasUnanswered) {
          setTimeout(() => {
            const currentIndex = pendingPillClarifications.findIndex(c => c.question === question);

            const reorderedClarifications = [
              ...pendingPillClarifications.slice(currentIndex + 1),
              ...pendingPillClarifications.slice(0, currentIndex)
            ];
            const nextUnanswered = reorderedClarifications.find(
              c => !getClarificationAnswer(newResponses[c.question])
            );

            if (nextUnanswered) {
              const scrollContainer = clarificationScrollRef.current;
              const labelValue = nextUnanswered.label || nextUnanswered.question;
              const targetElement = scrollContainer?.querySelector(
                `[data-clarification-label="${CSS.escape(labelValue)}"]`
              );
              if (targetElement && scrollContainer) {
                targetElement.scrollIntoView({ behavior: "smooth", block: "start" });
              }
            }
          }, 100);
        }

        return newResponses;
      });

      if (value !== null) {
        const clarification = pendingPillClarifications.find(c => c.question === question);
        if (clarification?.type === "finalize_confirmation") {
          autoSubmitFinalizeRef.current = true;
        }
      }
    },
    [pendingPillClarifications, currentArtworkId, currentSessionId, activePinId, activePin]
  );

  const handleToggleCustomInput = useCallback(
    (question: string) => {
      setClarificationResponses(prev => ({
        ...prev,
        [question]: {
          selected: prev[question]?.selected ?? null,
          custom: prev[question]?.custom ?? "",
          isTypingCustom: !prev[question]?.isTypingCustom
        }
      }));

      if (currentArtworkId && currentSessionId && activePinId && activePin) {
        trackDesignLabPinClarificationClicked({
          artworkId: currentArtworkId,
          sessionId: currentSessionId,
          pinId: activePinId,
          optionValue: "",
          turnIndex: activePin.messages.filter(m => m.role === "user").length + 1,
          userMessagesCount: activePin.messages.filter(m => m.role === "user").length,
          aiMessagesCount: activePin.messages.filter(m => m.role === "ai").length,
          optionType: "custom"
        });
      }
    },
    [currentArtworkId, currentSessionId, activePinId, activePin]
  );

  const handleCustomInputChange = useCallback((question: string, value: string) => {
    setClarificationResponses(prev => ({
      ...prev,
      [question]: {
        selected: prev[question]?.selected ?? null,
        custom: value,
        isTypingCustom: true
      }
    }));
  }, []);

  const formatClarificationResponses = useCallback(() => {
    const parts: string[] = [];
    const analyticsClarifications: AnalyticsClarification[] = [];

    for (const clarification of pendingPillClarifications) {
      const value = getClarificationAnswer(clarificationResponses[clarification.question]);
      const optionType = clarificationResponses[clarification.question]?.isTypingCustom
        ? "custom"
        : "suggested";
      analyticsClarifications.push({
        question: clarification.question,
        value: value || "",
        option_type: optionType
      });
      if (value) {
        const labelText = clarification.label || clarification.question;
        parts.push(`"${labelText}": ${value}`);
      }
    }

    return { clarificationText: parts.join("\n"), clarifications: analyticsClarifications };
  }, [pendingPillClarifications, clarificationResponses]);

  const hasAnyClarificationResponse = useMemo(
    () =>
      pendingPillClarifications.some(c =>
        getClarificationAnswer(clarificationResponses[c.question])
      ),
    [pendingPillClarifications, clarificationResponses]
  );

  const handleSubmit = useCallback(() => {
    if (!activePin) return;
    if (activePin.state === "processing") return;
    const sessionId = currentSessionId ?? null;

    const { clarificationText, clarifications } = formatClarificationResponses();
    const messageText = composerText.trim();
    const fullMessage = [clarificationText, messageText].filter(Boolean).join("\n");

    if (!fullMessage) return;

    const pinId = activePin.id;
    const now = Date.now();
    const userMessageId = createUuid();
    const isFirstMessage = activePin.messages.filter(m => m.role === "user").length === 0;
    const turnIndex = activePin.messages.filter(m => m.role === "user").length + 1;

    // Save state before submission for error recovery
    savedClarificationResponsesRef.current = { ...clarificationResponses };
    savedPinStateRef.current = activePin.state;
    savedMessagesRef.current = [...activePin.messages];
    savedComposerTextRef.current = composerText;

    updatePin(pinId, pin => ({
      ...pin,
      state: "processing" as const,
      thinkingText: undefined,
      messages: [
        ...pin.messages.map(m => ({ ...m, clarifications: undefined })),
        { id: userMessageId, role: "user" as const, content: fullMessage, timestamp: now }
      ]
    }));
    setComposerText("");
    setClarificationResponses({});
    setLastEditedPinId(pinId);

    const { userMessagesCount, aiMessagesCount } = getMessageCounts(activePin.messages, "user");

    if (currentArtworkId && currentSessionId) {
      trackDesignLabPinMessageSubmitted({
        artworkId: currentArtworkId,
        sessionId: currentSessionId,
        pinId,
        isFirstMessage,
        turnIndex,
        userMessagesCount,
        aiMessagesCount,
        clarifications
      });
    }

    // Condition B: long chat — 5+ user messages in single pin without reaching ready
    if (userMessagesCount >= 5 && activePin.state !== "ready") {
      setEscapeHatchTrigger(prev => prev ?? "long_chat");
    }

    if (!sessionId) {
      return;
    }

    // Condition B: slow response — start 30s timer, cleared on first SSE callback
    if (slowResponseTimerRef.current) {
      clearTimeout(slowResponseTimerRef.current);
    }
    slowResponseTimerRef.current = setTimeout(() => {
      setEscapeHatchTrigger(prev => prev ?? "slow_response");
      slowResponseTimerRef.current = null;
    }, 30_000);

    const priorPins = pins
      .filter(p => p.id !== pinId)
      .map(p => ({
        pin_id: p.id,
        coordinates: p.coordinates,
        state: p.state,
        instruction: p.instruction ?? ""
      }));

    abortControllerRef.current?.abort();
    abortControllerRef.current = new AbortController();

    submitMessage({
      sessionId,
      pinId,
      coordinates: activePin.coordinates,
      userMessage: fullMessage,
      isFirstMessage,
      priorPins,
      isPinActive: id => isPinActiveRef.current(id),
      signal: abortControllerRef.current.signal
    });
  }, [
    activePin,
    composerText,
    createUuid,
    currentArtworkId,
    currentSessionId,
    formatClarificationResponses,
    pins,
    setLastEditedPinId,
    submitMessage,
    updatePin,
    setEscapeHatchTrigger
  ]);

  // Auto-submit when a finalize_confirmation clarification option is selected.
  useEffect(() => {
    if (autoSubmitFinalizeRef.current && hasAnyClarificationResponse) {
      autoSubmitFinalizeRef.current = false;
      handleSubmit();
    }
  }, [hasAnyClarificationResponse, handleSubmit]);

  // Auto-submit initial message when first pin is placed (message-first flow).
  // Bypasses the composer state to avoid race conditions with the activePinId reset effect.
  const autoSubmitTriggeredRef = useRef(false);
  useEffect(() => {
    if (!pendingInitialMessage || autoSubmitTriggeredRef.current) return;
    if (pins.length === 0 || !activePinId) return;

    const pin = pins.find(p => p.id === activePinId);
    if (!pin || pin.state !== "draft" || pin.messages.length > 0) return;

    const sessionId = currentSessionId ?? null;
    if (!sessionId) return;

    autoSubmitTriggeredRef.current = true;
    const message = pendingInitialMessage;
    setPendingInitialMessage(null);

    const pinId = pin.id;
    const userMessageId = createUuid();
    const now = Date.now();

    savedPinStateRef.current = pin.state;
    savedMessagesRef.current = [...pin.messages];
    savedComposerTextRef.current = message;

    updatePin(pinId, p => ({
      ...p,
      state: "processing" as const,
      thinkingText: undefined,
      messages: [{ id: userMessageId, role: "user" as const, content: message, timestamp: now }]
    }));
    setLastEditedPinId(pinId);

    if (currentArtworkId && currentSessionId) {
      trackDesignLabPinMessageSubmitted({
        artworkId: currentArtworkId,
        sessionId: currentSessionId,
        pinId,
        isFirstMessage: true,
        turnIndex: 1,
        userMessagesCount: 1,
        aiMessagesCount: 0,
        clarifications: []
      });
    }

    const priorPins = pins
      .filter(p => p.id !== pinId)
      .map(p => ({
        pin_id: p.id,
        coordinates: p.coordinates,
        state: p.state,
        instruction: p.instruction ?? ""
      }));

    if (slowResponseTimerRef.current) {
      clearTimeout(slowResponseTimerRef.current);
    }
    slowResponseTimerRef.current = setTimeout(() => {
      setEscapeHatchTrigger(prev => prev ?? "slow_response");
      slowResponseTimerRef.current = null;
    }, 30_000);

    abortControllerRef.current?.abort();
    abortControllerRef.current = new AbortController();

    submitMessage({
      sessionId,
      pinId,
      coordinates: pin.coordinates,
      userMessage: message,
      isFirstMessage: true,
      priorPins,
      isPinActive: id => isPinActiveRef.current(id),
      signal: abortControllerRef.current.signal
    });
  }, [
    pendingInitialMessage,
    pins,
    activePinId,
    currentSessionId,
    currentArtworkId,
    setPendingInitialMessage,
    createUuid,
    updatePin,
    setLastEditedPinId,
    submitMessage,
    setEscapeHatchTrigger
  ]);

  // Mobile-specific effects
  useEffect(() => {
    if (!isDesktop && activePinId) {
      const pin = pins.find(p => p.id === activePinId);
      if (pin?.isNew && pin?.state === "draft" && pin?.messages.length === 0) {
        setMobileView("detail");
        setIsPanelExpanded(true);
      }
    }
  }, [isDesktop, activePinId, pins]);

  // Auto-select incomplete pin
  useEffect(() => {
    if (pins.length === 0) return;

    const incompletePin = pins.find(p => p.state !== "ready" && p.state !== "unsupported");
    if (!incompletePin) return;

    if (isDesktop) {
      if (!activePinId) {
        setActivePinId(incompletePin.id);
        // Scroll to the pin after DOM updates
        setTimeout(() => {
          const pinElement = pinsContainerRef.current?.querySelector(
            `[data-pin-id="${incompletePin.id}"]`
          );
          pinElement?.scrollIntoView({ behavior: "smooth", block: "center" });
        }, 100);
      }
    } else if (mobileView === "list") {
      setActivePinId(incompletePin.id);
      setMobileView("detail");
    }
  }, [isDesktop, pins, mobileView, activePinId, setActivePinId, setMobileView]);

  useEffect(() => {
    if (!isDesktop && pins.length === 0) {
      setMobileView("list");
    }
  }, [isDesktop, pins.length]);

  useEffect(() => {
    if (!isDesktop && mobileView === "detail" && activePin) {
      const isCompleted = activePin.state === "ready" || activePin.state === "unsupported";
      if (isCompleted) {
        setHoveredPinId(activePin.id);
        setHoveredPinSource("panel");
      }
    }
    return () => {
      if (!isDesktop) {
        setHoveredPinId(null);
        setHoveredPinSource(null);
      }
    };
  }, [isDesktop, mobileView, activePin, setHoveredPinId, setHoveredPinSource]);

  const handleApproveImage = useCallback(async () => {
    if (!imageApproval || !currentArtworkId || !currentDesignProjectPlacementId || isRejectingPin)
      return;

    try {
      const data = await approvePinMutate({
        sessionId: imageApproval.sessionId
      });

      const response = await fetch(imageApproval.imageUrl);
      if (!response.ok) throw new Error(`Image fetch failed: ${response.status}`);
      const blob = await response.blob();
      await completeRefine({
        dtf_artwork_id: Number(currentArtworkId),
        image: blob,
        manual_edit: true,
        remove_background: false,
        start_new_edit: false,
        ai_edited: true
      });

      setPinningSessions(prev => ({
        ...prev,
        [currentDesignProjectPlacementId]: {
          sessionId: imageApproval.sessionId,
          state: data.state ?? "approved",
          summary: data.summary ?? imageApproval.summary
        }
      }));
      trackPinSessionsGenConfirmed({
        artworkId: currentArtworkId,
        sessionId: imageApproval.sessionId,
        pinId: imageApproval.pinId
      });
      queryClient.invalidateQueries({ queryKey: ["dtfArtworks"] });
      setImageApproval(null);
      setCurrentPinningSession(null);
      setPinningMode(false);
    } catch (error) {
      console.error("[handleApproveImage] Failed:", error);
      showErrorToast({
        title: "Failed to save image",
        description: "Something went wrong. Please try again."
      });
    }
  }, [
    imageApproval,
    currentArtworkId,
    currentDesignProjectPlacementId,
    isRejectingPin,
    approvePinMutate,
    completeRefine,
    queryClient,
    setPinningSessions,
    setImageApproval,
    setCurrentPinningSession,
    setPinningMode,
    showErrorToast
  ]);

  const handleRejectImage = useCallback(async () => {
    if (!imageApproval || !currentArtworkId || !currentDesignProjectPlacementId || isApprovingPin)
      return;

    try {
      const data = await rejectPinMutate({
        sessionId: imageApproval.sessionId
      });

      setPinningSessions(prev => ({
        ...prev,
        [currentDesignProjectPlacementId]: {
          sessionId: imageApproval.sessionId,
          state: data.state ?? "completed",
          summary: data.summary ?? imageApproval.summary
        }
      }));
      trackPinSessionsGenDiscarded({
        artworkId: currentArtworkId,
        sessionId: imageApproval.sessionId,
        pinId: imageApproval.pinId
      });
      setImageApproval(null);
      setCurrentPinningSession(null);
      setPinningMode(false);
    } catch (error) {
      console.error("[handleRejectImage] Failed:", error);
      showErrorToast({
        title: "Failed to reject image",
        description: "Something went wrong. Please try again."
      });
    }
  }, [
    imageApproval,
    currentArtworkId,
    currentDesignProjectPlacementId,
    isApprovingPin,
    rejectPinMutate,
    setPinningSessions,
    setImageApproval,
    setCurrentPinningSession,
    setPinningMode,
    showErrorToast
  ]);

  const handleEscalationConfirm = useCallback(() => {
    if (!currentSessionId || !currentArtworkId || !currentDesignProjectPlacementId) {
      setPinningMode(false);
      return;
    }

    completeSession(currentSessionId, {
      onSuccess: data => {
        const readyPinsCount = pins.filter(
          p => p.state === "ready" || p.state === "unsupported"
        ).length;
        trackDesignLabPinningSessionCompleted({
          artworkId: currentArtworkId,
          sessionId: currentSessionId,
          pinsCount: pins.length,
          readyPinsCount,
          summary: data.summary || "",
          isStaff
        });
        setPinningSessions(prev => ({
          ...prev,
          [currentDesignProjectPlacementId]: {
            sessionId: currentSessionId,
            state: "completed",
            summary: data.summary ?? null
          }
        }));
        setCurrentPinningSession(null);
        setPinningMode(false);
      },
      onError: (error: Error) => {
        trackDesignLabPinningSessionCompletionFailed({
          artworkId: currentArtworkId,
          sessionId: currentSessionId,
          errorMessage: error?.message || "Unknown error"
        });
        showErrorToast({
          title: "Something went wrong",
          description: "Please try again."
        });
      }
    });
  }, [
    currentSessionId,
    currentArtworkId,
    currentDesignProjectPlacementId,
    completeSession,
    pins,
    isStaff,
    setPinningSessions,
    setCurrentPinningSession,
    setPinningMode,
    showErrorToast
  ]);

  const handleEscalationCancel = useCallback(
    (pinId: string) => {
      handleDeletePin(pinId);
    },
    [handleDeletePin]
  );

  const trackPinSelected = useCallback(
    (pinId: string, previousPinId: string | null, pinState: string) => {
      if (currentArtworkId && currentSessionId) {
        trackDesignLabPinSelected({
          artworkId: currentArtworkId,
          sessionId: currentSessionId,
          pinId,
          previousPinId,
          pinState,
          isStaff
        });
      }
    },
    [currentArtworkId, currentSessionId, isStaff]
  );

  return {
    // State
    isDesktop,
    pins,
    activePinId,
    activePin,
    composerText,
    clarificationResponses,
    removingPinIds,
    pendingDeletePinId,
    editingPinId,
    editingPin,
    isUpdatingPinInstruction,
    mobileView,
    isPanelExpanded,
    unansweredAbove,
    unansweredBelow,

    // Refs
    textareaRef,
    pendingCursorPositionRef,
    clarificationScrollRef,
    messagesScrollRef,
    pinsContainerRef,

    // Derived/Memos
    pendingPillClarifications,
    selectedFontValue,
    selectedColorValue,
    hasAnyClarificationResponse,
    lastAiMessageWithClarifications,

    // Setters
    setActivePinId,
    setComposerText,
    setMobileView,
    setIsPanelExpanded,
    setHoveredPinId,
    setHoveredPinSource,

    // Handlers
    handleDeletePin,
    openDeletePinModal,
    closeDeletePinModal,
    confirmDeletePin,
    openEditPinModal,
    closeEditPinModal,
    handleUpdatePinInstruction,
    handleSelectClarificationOption,
    handleToggleCustomInput,
    handleCustomInputChange,
    handleSubmit,
    isComposerTextValid,
    calculateUnansweredPositions,
    scrollToFirstUnanswered,
    trackPinSelected,
    handleApproveImage,
    handleRejectImage,
    isApprovingImage,
    isImageLoading,
    isRejectingImage,
    imageApproval,
    handleEscalationConfirm,
    handleEscalationCancel,
    isCompletingSession
  };
}
